(function () {
	'use strict';

	// Cards on the same row never line up to the pixel, so a row is everything
	// whose top is within a couple of pixels of the first card's.
	var ROW_TOLERANCE = 2;

	// Every section measures its cards, then writes a class, a label and a
	// max-height. Done section by section, each write invalidates the layout the
	// next section's measure has to compute again: with a section per tag that
	// is a full layout of the page per section, and several times over while the
	// page loads. The sections hand their request to this queue instead, and a
	// frame measures all of them before writing any: one layout for the lot.
	var pending = new Set();
	var frame = null;

	function schedule(section) {
		pending.add(section);
		if (frame === null) {
			frame = requestAnimationFrame(flush);
		}
	}

	function flush() {
		frame = null;
		var batch = Array.from(pending);
		pending.clear();
		var measures = batch.map(function (section) {
			return section.measure();
		});
		batch.forEach(function (section, index) {
			if (measures[index]) {
				section.commit(measures[index]);
			}
		});
	}

	pjFold.$inject = ['gettextCatalog', '$timeout'];
	function pjFold(gettextCatalog, $timeout) {
		return {
			restrict: 'A',
			link: link,
		};

		// The attribute is the enabling expression: the flat layout renders one
		// headerless section holding everything, which must never be clamped to
		// a single row.
		function link(scope, element, attrs) {
			if (!scope.$eval(attrs.pjFold)) {
				return;
			}
			const block = element[0];
			const cards = block.querySelector('.dc-cards');
			if (!cards) {
				return;
			}

			let expanded = false;
			// While the height is in flight the observers must not write over it.
			let animating = false;
			// The dock slides in and out beside the grid, so every frame of that
			// resizes this block. Measuring costs a rect per card, so the page
			// says when it starts and when it has landed, and nothing is measured
			// in between.
			let docking = false;
			let fallback;

			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'pj-fold__btn';
			button.addEventListener('click', toggle);

			const icon = document.createElement('i');
			icon.setAttribute('aria-hidden', 'true');
			const label = document.createElement('span');
			button.appendChild(icon);
			button.appendChild(label);

			// Width changes re-flow the cards and change how many fit on a row, so
			// the clamp is re-measured rather than kept. The collapsed height pins
			// the box, so only the width observation is reliable here.
			const section = { measure: measure, commit: commit };
			let destroyed = false;
			const request = function () {
				schedule(section);
			};

			const resizeObserver = new ResizeObserver(request);
			resizeObserver.observe(cards);
			// Belt and braces: a viewport change is what re-flows the cards, and
			// the observer is the only thing watching for it.
			window.addEventListener('resize', request);

			// A clamped box does not change size when cards are added below the
			// fold, so the card count is watched separately.
			const mutationObserver = new MutationObserver(request);
			mutationObserver.observe(cards, { childList: true });

			// The cards are rendered by an ng-repeat that has not run yet at link
			// time.
			$timeout(request, 0, false);

			scope.$on('pj:dock-transition', function (event, active) {
				docking = !!active;
				if (!docking) {
					request();
				}
			});

			scope.$on('$destroy', function () {
				destroyed = true;
				pending.delete(section);
				resizeObserver.disconnect();
				mutationObserver.disconnect();
				window.removeEventListener('resize', request);
				button.removeEventListener('click', toggle);
				$timeout.cancel(fallback);
			});

			// Both ends of the height are known - the measured clamp and the
			// content height - so the box animates between two pixel values
			// rather than to or from 'none', which does not transition.
			function toggle() {
				expanded = !expanded;
				chrome();
				const measured = rows();
				if (!measured.overflowing) {
					setMaxHeight('');
					return;
				}
				const clamped = Math.floor(measured.height) + 'px';
				const full = cards.scrollHeight + 'px';
				animating = true;
				block.classList.add('pj-group--animating');
				setMaxHeight(expanded ? clamped : full);
				// Reading the layout commits the start value before the target
				// is written, so the two are distinct frames and it animates.
				void cards.offsetWidth;
				setMaxHeight(expanded ? full : clamped);
				cards.addEventListener('transitionend', settle);
				// transitionend never fires if the two ends coincide.
				fallback = $timeout(settle, 400, false);
			}

			function settle(event) {
				if (event && event.propertyName !== 'max-height') {
					return;
				}
				cards.removeEventListener('transitionend', settle);
				$timeout.cancel(fallback);
				animating = false;
				block.classList.remove('pj-group--animating');
				request();
			}

			// Clipped cards keep their layout, so the rows below the fold are
			// still measurable while collapsed. The clamp lands where the second
			// row starts: the first row and the grid gap below it, and no part of
			// the next row.
			function rows() {
				const items = cards.children;
				const measured = { height: 0, overflowing: false };
				if (!items.length) {
					return measured;
				}
				const base = cards.getBoundingClientRect().top;
				const first = items[0].getBoundingClientRect().top;
				for (let i = 0; i < items.length; i++) {
					const rect = items[i].getBoundingClientRect();
					if (rect.top > first + ROW_TOLERANCE) {
						measured.overflowing = true;
						measured.height = rect.top - base;
						return measured;
					}
					measured.height = Math.max(measured.height, rect.bottom - base);
				}
				return measured;
			}

			// Only the wording change animates - a request comes in on every resize and
			// mutation, and restarting the fade each time would flicker.
			function setLabel(text) {
				if (label.textContent === text) {
					return;
				}
				const first = label.textContent === '';
				label.textContent = text;
				label.classList.remove('pj-fold__label--swap');
				// Reading the layout restarts the animation. The first wording has
				// none to restart, and the read would cost a layout per section.
				if (!first) {
					void label.offsetWidth;
				}
				label.classList.add('pj-fold__label--swap');
			}

			// Guarded: writing the height resizes the box the observer watches.
			function setMaxHeight(value) {
				if (cards.style.maxHeight === value) {
					return;
				}
				cards.style.maxHeight = value;
			}

			// The header is rendered by an ng-if, so it is not there to hold the
			// button at link time.
			function place() {
				if (button.parentNode) {
					return;
				}
				const head = block.querySelector('.pj-group__head');
				if (head) {
					head.appendChild(button);
				}
			}

			function chrome() {
				block.classList.toggle('pj-group--expanded', expanded);
				icon.className = expanded
					? 'fas fa-chevron-up'
					: 'fas fa-chevron-down';
				setLabel(
					expanded
						? gettextCatalog.getString('project.group.fold')
						: gettextCatalog.getString('project.group.unfold')
				);
			}

			// The reads of a request, run by the queue before any section writes.
			function measure() {
				if (destroyed || animating || docking) {
					return null;
				}
				return rows();
			}

			// The writes of a request, with what measure() found.
			function commit(measured) {
				if (destroyed || animating || docking) {
					return;
				}
				place();
				block.classList.toggle(
					'pj-group--overflowing',
					measured.overflowing
				);
				chrome();
				// Floored, not ceiled: the clamp sits exactly where the next row
				// starts, so rounding up reveals the top edge of its cards.
				setMaxHeight(
					measured.overflowing && !expanded
						? Math.floor(measured.height) + 'px'
						: ''
				);
			}
		}
	}

	angular.module('dcApp').directive('pjFold', pjFold);
})();
